// Top Targets widget: the stage mapping and the duplicate check, in one place.
// The database keeps its old stage words (target / active / cold) and the new 'warming'; the widget shows
// Researching / Warming Up / Engaged / Parked. Nothing is rewritten in the table.
import { supabase } from '../../lib/supabase';
import { companyKey } from '../../lib/companyName';

import { fetchAllRows } from '../../lib/fetchAll';
export const STAGES = [
  { key: 'researching', label: 'Researching', db: 'target' },
  { key: 'warming', label: 'Warming Up', db: 'warming' },
  { key: 'engaged', label: 'Engaged', db: 'active' },
  { key: 'parked', label: 'Parked', db: 'cold' },
];
export const stageOf = (db) => STAGES.find((s) => s.db === db) || STAGES[0]; // unknown values read as Researching
export const dbOf = (key) => (STAGES.find((s) => s.key === key) || STAGES[0]).db;

// What a row shows as its key contact: the Primary role if there is one, else the first.
export function keyContact(contacts) {
  const list = contacts || [];
  return list.find((c) => String(c.role || '').toLowerCase() === 'primary') || list[0] || null;
}

export const normName = (s) => String(s || '').toLowerCase().replace(/[.,]/g, ' ').replace(/\b(inc|llc|l l c|ltd|corp|corporation|co|company|lp|llp|plc)\b/g, ' ').replace(/\s+/g, ' ').trim();
export const normDomain = (s) => String(s || '').toLowerCase().trim().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];

// ── The company door (POST /api/add-to-charlie, Viper's PR #672) ──
// Check first (no write), then create. The door follows succession (Marathon to ConocoPhillips, Hess to Chevron...), finds a company
// that already exists, and treats near names ("Devon" / "Devon Energy") as possible duplicates unless overridden.
async function doorPost(body) {
  try {
    const { data: sess } = await supabase.auth.getSession();
    const res = await fetch('/api/add-to-charlie', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sess?.session?.access_token}` },
      body: JSON.stringify(body),
    });
    let data = {};
    try { data = await res.json(); } catch (e) { /* non-JSON error page */ }
    return { httpOk: res.ok, status: res.status, data };
  } catch (e) {
    return { httpOk: false, status: 0, data: {} };
  }
}

// The old browser-side check, kept as the fallback if the door cannot be reached.
export async function checkCompanyLocal({ name, domain }) {
  const n = normName(name);
  const k = companyKey(name); // the shared normalizer decides "the same company" for Charlie's own list
  const d = normDomain(domain);
  if (!n && !d) return { status: 'new', match: null };
  // the whole lists, every page (a .limit(5000) is still cut at 1000 rows, so a company past row 1000 looked new)
  const [accRows, coRows] = await Promise.all([
    fetchAllRows(supabase, 'scout_accounts', { select: 'id, name', order: ['name'], label: 'Target check accounts' }),
    fetchAllRows(supabase, 'companies', { select: 'id, name, website, email_domain', filter: (f) => f.is('deleted_at', null).is('merged_into_id', null), order: ['name'], label: 'Target check companies' }),
  ]);
  const acc = { data: accRows };
  const cos = { data: coRows };
  const a = (acc.data || []).find((x) => normName(x.name) === n);
  if (a) return { status: 'already_target', match: { id: a.id, name: a.name } };
  const c = (cos.data || []).find((x) => (k && companyKey(x.name) === k) || (d && (normDomain(x.website) === d || normDomain(x.email_domain) === d)));
  if (c) return { status: 'already_in_charlie', match: { id: c.id, name: c.name } };
  return { status: 'new', match: null };
}

// Maps the door's answer to what the widget shows:
//   new                 nothing matches
//   already_target      it is already a target (match.stage says where)
//   company_no_target   the company exists but is not a target yet: adding it as a target is fine
//   possible_duplicate  a near name or an open duplicate pair: show the companies, let the person override
export function mapCheck(data, typedName) {
  if (!data || data.ok === false) return null;
  const succession = data.succession || null;
  if (data.status === 'new') return { status: 'new', match: null, succession };
  if (data.status === 'possible_duplicate') return { status: 'possible_duplicate', match: { companies: (data.match && data.match.companies) || [] }, succession };
  if (data.status === 'already_exists') {
    if (data.target && data.target.id) return { status: 'already_target', match: { id: data.target.id, name: data.target.name || typedName, stage: data.target.stage }, succession };
    return { status: 'company_no_target', match: { id: data.company_id, name: typedName }, succession };
  }
  return null;
}

export async function checkCompany({ name, domain, partnerId }) {
  const r = await doorPost({ source: 'target_quick_add', action: 'check', company: { name, website: domain || undefined }, options: { partner_id: partnerId } });
  const mapped = r.httpOk ? mapCheck(r.data, name) : null;
  if (mapped) return mapped;
  return checkCompanyLocal({ name, domain }); // door unreachable or unexpected answer: fall back to the browser-side check
}

// Create through the door. Returns { ok, status, company_id, target, succession } or { error }.
export async function addCompany({ name, domain, partnerId, allowPossibleDuplicate = false, notes }) {
  const r = await doorPost({ source: 'target_quick_add', company: { name: name.trim(), website: domain || undefined, notes: notes || undefined }, options: { partner_id: partnerId, stage: 'target', allow_possible_duplicate: allowPossibleDuplicate || undefined } });
  if (!r.httpOk || r.data.ok === false) return { error: r.data.error || r.data.reason || `Could not add (${r.status || 'offline'}).` };
  return r.data;
}

// Log a Sighting (a company spotted on the road): the door resolves or creates the company without making a target, the sighting is
// written to bird_dog_sightings (kept as the record of where and when), and the company becomes a Researching target.
export async function logSighting({ name, spottedWhere, notes, basin, partnerId, allowPossibleDuplicate = false }) {
  const sight = await doorPost({ source: 'sighting', company: { name: name.trim(), notes: notes || undefined }, options: { partner_id: partnerId, allow_possible_duplicate: allowPossibleDuplicate || undefined } });
  if (!sight.httpOk || sight.data.ok === false) return { error: sight.data.error || sight.data.reason || `Could not log (${sight.status || 'offline'}).` };
  if (sight.data.status === 'possible_duplicate') return { status: 'possible_duplicate', match: sight.data.match, succession: sight.data.succession || null };
  const companyId = sight.data.company_id || null;
  const { error } = await supabase.from('bird_dog_sightings').insert({
    company_name: name.trim(), spotted_where: spottedWhere || null, spotted_on: new Date().toISOString().slice(0, 10), notes: notes || null,
    basin_or_location: basin || null, potential_partner_id: partnerId || null, promoted_company_id: companyId, promoted_at: companyId ? new Date().toISOString() : null, status: 'new',
  });
  if (error) return { error: error.message };
  const tgt = await addCompany({ name, partnerId, allowPossibleDuplicate, notes: [spottedWhere && `Spotted: ${spottedWhere}`, notes].filter(Boolean).join('. ') || undefined });
  if (tgt.error) return { status: 'logged', company_id: companyId, succession: sight.data.succession || null, target_error: tgt.error };
  return { status: 'logged', company_id: companyId, target: tgt.target || null, succession: sight.data.succession || tgt.succession || null };
}

const COLS = 'id, name, stage, notes, owner, partner_id, basin, next_step, last_touch_at, updated_at, partners(name), scout_contacts(id, name, role, email, phone)';

export async function loadTargets() {
  const { data, error } = await supabase.from('scout_accounts').select(COLS).order('sort_order', { ascending: true }).limit(500);
  if (error) return { error: error.message };
  return { accounts: data || [] };
}

export async function updateTarget(id, patch) {
  const { error } = await supabase.from('scout_accounts').update(patch).eq('id', id);
  return error ? { error: error.message } : { ok: true };
}
